import { useState } from "react";
import { Spinner } from "reactstrap";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import { toast } from "react-toastify";
import { StatusChip } from "../common/StatusChip";

const STAMP_META = {
  Draft: { tone: "neutral", label: "Borrador" },
  Pending: { tone: "warning", label: "Pendiente" },
  Sent: { tone: "info", label: "Enviado" },
  Accepted: { tone: "success", label: "Aceptado" },
  Rejected: { tone: "danger", label: "Rechazado" },
};

const Row = ({ label, children }) => (
  <div className="ui-kv__row">
    <span className="ui-kv__label">{label}</span>
    <span className="ui-kv__value">{children}</span>
  </div>
);

export const CardSiigoInvoice = ({ order, onOrderUpdate }) => {
  const { postData } = useDataFetch();
  const [retryingInvoice, setRetryingInvoice] = useState(false);

  if (!order) return null;

  const { mutateAsync: retrySiigoInvoiceMutation } = postData(
    `/order/retry-siigo-invoice/${order.id}`,
    {},
    {
      onSuccess: (response) => {
        if (response.data?.order) {
          toast.success("Factura generada exitosamente");
          onOrderUpdate?.(response.data.order);
        } else {
          toast.error(response.errors || "Error al generar factura");
        }
        setRetryingInvoice(false);
      },
      onError: (error) => {
        console.error("Error al reintentar factura:", error);
        toast.error("Error al generar factura. Intente nuevamente.");
        setRetryingInvoice(false);
      },
    }
  );

  const retrySiigoInvoice = async () => {
    setRetryingInvoice(true);
    await retrySiigoInvoiceMutation();
  };

  const stamp = order.siigo_invoice?.stamp?.status;
  const stampMeta = stamp ? STAMP_META[stamp] || { tone: "neutral", label: stamp } : null;

  return (
    <div className="ui-card">
      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 15 }}>
        <i className="ri-bill-line" style={{ color: "#4B36F0", fontSize: 16 }} />
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Factura Siigo</h3>
      </div>

      {!order.is_paid ? (
        <div className="ui-note ui-note--warning">
          <i className="ri-error-warning-line ui-note__icon" />
          <div>
            <div className="ui-note__title">Sin facturar</div>
            <div className="ui-note__body">La factura se genera cuando la orden pasa a Pagada.</div>
          </div>
        </div>
      ) : order.siigo_invoice_status === "success" && order.siigo_invoice ? (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <StatusChip tone="success" icon="ri-checkbox-circle-line">Generada</StatusChip>
            {order.siigo_invoice.name && (
              <span style={{ fontWeight: 700, fontSize: 13 }}>{order.siigo_invoice.name}</span>
            )}
          </div>
          <div style={{ display: "flex", gap: 9, flexWrap: "wrap", marginBottom: 14 }}>
            {order.siigo_invoice.pdf_url && (
              <a href={order.siigo_invoice.pdf_url} target="_blank" rel="noopener noreferrer" className="ui-note__body" style={{ color: "#4B36F0", fontWeight: 600, fontSize: 12.5 }}>
                <i className="ri-download-2-line me-1" /> Descargar PDF
              </a>
            )}
            {order.siigo_invoice.public_url && (
              <a href={order.siigo_invoice.public_url} target="_blank" rel="noopener noreferrer" style={{ color: "#4B36F0", fontWeight: 600, fontSize: 12.5 }}>
                <i className="ri-file-text-line me-1" /> Ver en Siigo
              </a>
            )}
          </div>
          <div className="ui-kv">
            {order.siigo_invoice.id && <Row label="ID Siigo"><span className="ui-kv__value--mono">{order.siigo_invoice.id}</span></Row>}
            {stamp && (
              <Row label="Estado DIAN">
                <StatusChip tone={stampMeta.tone} sm>{stampMeta.label}</StatusChip>
              </Row>
            )}
            {order.siigo_invoice.stamp?.cufe && (
              <Row label="CUFE"><span className="ui-kv__value--mono ui-kv__value--muted">{order.siigo_invoice.stamp.cufe}</span></Row>
            )}
            {order.siigo_invoice.mail && (
              <Row label="Envío por email">
                <StatusChip tone={order.siigo_invoice.mail.status === "sent" ? "success" : "warning"} sm>
                  {order.siigo_invoice.mail.status === "sent" ? "Enviado" : "No enviado"}
                </StatusChip>
              </Row>
            )}
            {order.siigo_invoice.metadata?.created && (
              <Row label="Fecha de creación">
                {new Date(order.siigo_invoice.metadata.created).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" })}
              </Row>
            )}
          </div>
        </>
      ) : order.siigo_invoice_status === "error" ? (
        <>
          <div className="ui-note ui-note--warning" style={{ background: "#FEF4F4", borderColor: "#F6C9C9", color: "#DC2626", marginBottom: 12 }}>
            <i className="ri-close-circle-line ui-note__icon" />
            <div>
              <div className="ui-note__title" style={{ color: "#DC2626" }}>Error al generar</div>
              {order.siigo_invoice_error?.response?.Errors && (
                <div className="ui-note__body">
                  {order.siigo_invoice_error.response.Errors.map((err, idx) => (
                    <div key={idx}>{err.Message}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
          {order.siigo_invoice_error?.timestamp && (
            <div className="ui-kv" style={{ marginBottom: 12 }}>
              <Row label="Fecha del error">
                {new Date(order.siigo_invoice_error.timestamp).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" })}
              </Row>
            </div>
          )}
          <button
            type="button"
            className="btn"
            style={{ width: "100%", border: "1px solid #E1E4F0", background: "#fff", color: "#3B4266", borderRadius: 9, padding: "11px 0", fontFamily: "inherit", fontSize: 13, fontWeight: 600 }}
            onClick={retrySiigoInvoice}
            disabled={retryingInvoice}
          >
            {retryingInvoice ? <><Spinner size="sm" className="me-1" /> Reintentando...</> : <><i className="ri-refresh-line me-1" /> Reintentar factura</>}
          </button>
        </>
      ) : (
        <div className="ui-note ui-note--warning">
          <i className="ri-error-warning-line ui-note__icon" />
          <div>
            <div className="ui-note__title">Pendiente de generación</div>
          </div>
        </div>
      )}
    </div>
  );
};
